import * as React from 'react';
import { useState, useEffect } from 'react';
import { PopularProducts } from './SearchHeaderBlocks/PopularProducts';
import shopify from 'frontend/js/shopify';
import api from 'frontend/js/api';

import { useSearchHeader } from 'frontend/src/contexts/SearchHeaderContext';

// Hardcoded limit for popular products
const POPULAR_PRODUCTS_LIMIT = 10;

interface PopularProductsBlockProps {
  settings?: {
    popular_products_title?: string;
    tile_type?: string;
    popular_products_collection?: any;
    collection?: any;
    [key: string]: any;
  };
  className?: string;
  searchQuery?: string;
}

export const PopularProductsBlock: React.FC<PopularProductsBlockProps> = ({ settings, className = '' }) => {

  const blockSettings = settings?.settings?.block_settings || settings || {};
  const [popularProducts, setPopularProducts] = useState([]);

  // Use SearchHeaderContext for searchQuery
  const { searchQuery } = useSearchHeader();
  // Track if we should show popular products (when query is empty)
  const showPopularProducts = !searchQuery || searchQuery.length === 0;

  const handleViewMore = () => {
    const collectionHandle = blockSettings.collection || blockSettings.popular_products_collection;
    if (collectionHandle) {
      window.location.href = `/collections/${collectionHandle}`;
    } else {
      window.location.href = '/collections/all';
    }
  };

  useEffect(() => {
    if (showPopularProducts) {
      const fetchPopularProducts = async () => {
        const collectionHandle = blockSettings.collection || blockSettings.popular_products_collection;
        if (!collectionHandle) {
          console.warn('No collection handle provided for popular products');
          setPopularProducts([]);
          return;
        }
        try {
          const products = await fetchProductsFromCollection(collectionHandle, POPULAR_PRODUCTS_LIMIT);
          setPopularProducts(products || []);
        } catch (error) {
          console.error('Error fetching popular products:', error);
          setPopularProducts([]);
        }
      };
      fetchPopularProducts();
    } else {
      setPopularProducts([]);
    }
  }, [blockSettings.collection, blockSettings.popular_products_collection, showPopularProducts]);

  // Function to fetch products from a Shopify collection
  const fetchProductsFromCollection = async (handle: string, first: number) => {
    const endpoint = '/api/2025-07/graphql.json';
    const accessToken = api.getStorefrontToken();

    // Simplified GraphQL query for fetching collection products
    const query = `
      query GetCollectionProducts($handle: String!, $first: Int!) {
        collectionByHandle(handle: $handle) {
          products(first: $first, sortKey: MANUAL, reverse: false) {
            edges {
              node {
                id
                title
                handle
                tags
                productType
                createdAt
                totalInventory
                vendor
                featuredImage {
                  altText
                  url
                }
                images(first: 5) {
                  edges {
                    node {
                      altText
                      url
                    }
                  }
                }
                priceRange {
                  maxVariantPrice {
                    amount
                    currencyCode
                  }
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                }
                variants(first: 10) {
                  edges {
                    node {
                      id
                      availableForSale
                      sku
                      selectedOptions {
                        name
                        value
                      }
                      price {
                        amount
                        currencyCode
                      }
                      compareAtPrice {
                        amount
                        currencyCode
                      }
                      image {
                        altText
                        url
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const variables = {
      handle: handle,
      first: first
    };

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': accessToken
        },
        body: JSON.stringify({
          query: query,
          variables: variables
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const collectionData = result?.data?.collectionByHandle?.products;

      if (!collectionData) {
        console.warn(`Collection '${handle}' not found or has no products`);
        return [];
      }

      // Transform the products using the shopify utility
      const productNodes = collectionData.edges.map((edge: any) => edge.node);
      const transformedProducts = productNodes.map((product: any) => 
        shopify.transformGraphqlProduct(product)
      );

      return transformedProducts;
    } catch (error) {
      console.error('Error fetching products from collection:', error);
      return [];
    }
  };

  return (
    <>
      {showPopularProducts && (
        <div className={`w-full ${className}`}>
          <PopularProducts
            title={blockSettings.popular_products_title || blockSettings.title || 'Popular Products'}
            products={popularProducts}
            settings={settings}
            onViewMore={handleViewMore}
          />
        </div>
      )}
    </>
  );
};
